import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Switch, Text, useDisclosure } from '@bonusly/design-system';

import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalThemeModal } from './seasonal-theme-modal';

interface SeasonalToggleProps {
	isExpanded?: boolean;
}

export const SeasonalToggle: FC<SeasonalToggleProps> = ({ isExpanded = true }) => {
	const { t } = useTranslation('components');
	const { hasSeasonalThemePermission, isSeasonalThemeEnabled, toggleTheme } = useSeasonalTheme();
	const { isOpen, onOpen, onClose } = useDisclosure();

	// Only show toggle if user has permission (seasonal-themes feature flag) and sidebar is expanded
	// This is completely independent of the dark-mode-beta feature flag
	if (!hasSeasonalThemePermission || !isExpanded) {
		return null;
	}

	const handleToggle = () => {
		if (isSeasonalThemeEnabled) {
			// If enabled, just toggle it off
			toggleTheme();
		} else {
			// If disabled, show the modal first
			onOpen();
		}
	};

	return (
		<>
			<Flex
				w="100%"
				alignItems="center"
				justifyContent="space-between"
				py={2}
				px={2.5}
				borderRadius="md"
				bg={isSeasonalThemeEnabled ? 'primary.bgAlpha' : 'transparent'}
				cursor="pointer"
				onClick={handleToggle}
				opacity={isExpanded ? 1 : 0}
				transition="all 0.15s cubic-bezier(.4, 0, .2, 1)"
				_hover={{
					bg: isSeasonalThemeEnabled ? 'primary.bgAlphaHover' : 'secondary.bgAlpha',
				}}
				role="button"
				aria-label={isSeasonalThemeEnabled ? t('seasonal_theme.toggle.disable_label') : t('seasonal_theme.toggle.enable_label')}
			>
				<Flex alignItems="center" gap={3}>
					<Text fontSize="md" lineHeight={1}>
						❄️
					</Text>
					<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap" userSelect="none">
						{t('seasonal_theme.toggle.label', { defaultValue: 'Holiday theme' })}
					</Text>
				</Flex>
				<Box onClick={(e) => e.stopPropagation()}>
					<Switch
						size="sm"
						isChecked={isSeasonalThemeEnabled}
						onChange={handleToggle}
						colorScheme="blue"
						aria-label={isSeasonalThemeEnabled ? t('seasonal_theme.toggle.disable_label') : t('seasonal_theme.toggle.enable_label')}
					/>
				</Box>
			</Flex>

			<SeasonalThemeModal isOpen={isOpen} onClose={onClose} onEnable={toggleTheme} />
		</>
	);
};
